import { ArrowRightIcon, Button, Chip } from '@bp/ui-components';
import classNames from 'classnames';
import { observer } from 'mobx-react-lite';
import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FindingSlotHighlight } from '../../../stores/TimetableVersion/PinboardStoreTypes';
import { highestSeverity, severityToken } from '../../../utils/timetable/severity';
import { GenericWarning } from '../../../utils/timetable/types';
import { useTimetableStore } from '../TimetableProvider';
import styles from './TestWarningGroup.module.scss';

type TestWarningGroupProps = {
  type: string;
  warnings: GenericWarning[];
  /** Show the "Im Plan zeigen" jump button (off where the board isn't visible, e.g. the wizard pre-flight). */
  showInPlanEnabled?: boolean;
};

export const TestWarningGroup: FC<TestWarningGroupProps> = observer(({ type, warnings, showInPlanEnabled = true }) => {
  const { t } = useTranslation();
  const pinboardStore = useTimetableStore();
  const [expand, setExpand] = useState(false);

  const typeToTranslation: Record<string, string> = {
    availability: t('pinboard.testModal.availability'),
    availabilityGap: t('pinboard.testModal.availabilityGap'),
    cardCannotBePlaced: t('pinboard.testModal.cardCannotBePlaced'),
    classTooManyCards: t('pinboard.testModal.classTooManyCards'),
    dependency: t('pinboard.testModal.dependency'),
    doubleLessonsWithoutPauses: t('pinboard.testModal.doubleLessonsWithoutPauses'),
    lessonTooManyCards: t('pinboard.testModal.lessonTooManyCards'),
    lonelyGroup: t('pinboard.testModal.lonelyGroup'),
    maxOnUnknownPositionExceeded: t('pinboard.testModal.maxOnUnknownPositionExceeded'),
    missingLunchBreak: t('pinboard.testModal.missingLunchBreak'),
    multiSlotCardCannotBePlaced: t('pinboard.testModal.multiSlotCardCannotBePlaced'),
    noReliableTimes: t('pinboard.testModal.noReliableTimes'),
    pinnedCardConflict: t('pinboard.testModal.pinnedCardConflict'),
    roomSupplyTooSmall: t('pinboard.testModal.roomSupplyTooSmall'),
    roomTooManyCards: t('pinboard.testModal.roomTooManyCards'),
    roomTotalTooManyCards: t('pinboard.testModal.roomTotalTooManyCards'),
    slotBottleneck: t('pinboard.testModal.slotBottleneck'),
    subjectTooManyCards: t('pinboard.testModal.subjectTooManyCards'),
    teacherTooManyCards: t('pinboard.testModal.teacherTooManyCards'),
    teacherTotalTooManyCards: t('pinboard.testModal.teacherTotalTooManyCards'),
  };

  useEffect(() => {
    setExpand(pinboardStore.controlMode.expand);
  }, [pinboardStore.controlMode.expand]);

  // Backend-Texte kommen fertig übersetzt (mit Leerzeichen), Abhängigkeits-Warnungen liefern
  // i18n-Keys (ohne Leerzeichen) — nur letztere durch i18next schicken
  const translateIfKey = (value: string) => (value && !value.includes(' ') ? t(value) : value);

  const collisionsOfWarning = (warning: GenericWarning) =>
    [
      warning.involvedClasses,
      warning.involvedSubjects,
      warning.involvedTeachers,
      warning.involvedRooms,
      warning.involvedGroups,
      warning.notMatchingDivisions,
    ].flatMap((collisions) => collisions ?? []);

  // nur UUIDs, die wirklich eine Karte im Board treffen — sonst dimmt der Modus alles
  // und hebt nichts hervor
  const cardUuidsOfWarning = (warning: GenericWarning) => [
    ...new Set(
      collisionsOfWarning(warning)
        .flatMap((collision) => collision.cards)
        .filter((uuid) => !!pinboardStore.getCardByUuid(uuid)),
    ),
  ];

  // Zell-Highlights der Warnung: jede Slot-Referenz landet in der Zeile ihrer Klasse UND in den
  // Zeilen der betroffenen Entitäten (Lehrer/Raum/…) — je nach Board-Kontext greift die passende.
  const slotHighlightsOfWarning = (warning: GenericWarning): FindingSlotHighlight[] => {
    if (!warning.slots || warning.slots.length === 0) {
      return [];
    }
    const entityUuids = collisionsOfWarning(warning).flatMap((collision) => collision.conflict);
    const hint = translateIfKey(warning.text).replace(/<[^>]+>/g, '');
    return warning.slots.flatMap((slot) => {
      const rowUuids = new Set<string>([...(slot.classUuid ? [slot.classUuid] : []), ...entityUuids]);
      return [...rowUuids].map((rowUuid) => ({
        rowUuid,
        hourUuid: slot.timegridEntryUuid,
        day: slot.weekday as FindingSlotHighlight['day'],
        hint,
      }));
    });
  };

  const showInPlan = (cardUuids: string[], slotHighlights: FindingSlotHighlight[]) => {
    pinboardStore.setControlModeActive(true);
    pinboardStore.addFindingHighlights(cardUuids);
    pinboardStore.addFindingSlotHighlights(slotHighlights);
    void pinboardStore.setTestModalFromGenerator(false);
    void pinboardStore.setTestModalOpen(false);
  };

  const groupLevel = highestSeverity(warnings.map((warning) => warning.priority));
  const groupToken = groupLevel ? severityToken(groupLevel) : null;

  const classes = classNames(styles['test-warning-group'], {
    [styles.open]: expand,
  });

  const header = typeToTranslation[type] || type;

  return (
    <div className={classes}>
      <div className={styles.headline} onClick={() => setExpand(!expand)}>
        <ArrowRightIcon className={classNames(styles.indicator, 'small')} />
        {groupToken && (
          <div className={styles.priority} title={t(groupToken.labelKey)}>
            <groupToken.Icon className='small' color={groupToken.color} />
          </div>
        )}
        <div className={styles.text}>{header}</div>
        <Chip value={warnings.length} />
      </div>
      {expand && (
        <div className={styles.detail}>
          {warnings.map((warning, wIndex) => {
            const cardUuids = cardUuidsOfWarning(warning);
            const slotHighlights = slotHighlightsOfWarning(warning);
            return (
              <div key={wIndex} className={styles.warningItem}>
                <div>
                  {/* Label kann Backend-HTML enthalten → dangerouslySetInnerHTML; involvedLabel sind
                      Nutzer-Namen und werden als (escapter) Textknoten angehängt. */}
                  <span dangerouslySetInnerHTML={{ __html: translateIfKey(warning.text) }} />
                  {warning.involvedLabel && <span> {warning.involvedLabel}</span>}
                </div>
                {warning.reason && <div className={styles.reason}>{translateIfKey(warning.reason)}</div>}
                {showInPlanEnabled && (cardUuids.length > 0 || slotHighlights.length > 0) && (
                  <Button
                    className={styles.action}
                    hierarchy='tertiary'
                    onClick={() => showInPlan(cardUuids, slotHighlights)}
                  >
                    {t('pinboard.testModal.showInPlan')}
                  </Button>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
});
